import React, {
  MutableRefObject,
  ReactNode,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useLocation } from 'react-router-dom';
import { CSSTransition } from 'react-transition-group';
import styled from 'styled-components';

import BackIcon from '#/components/basic/icons/BackIcon';
import MaterialSymbol from '#/components/basic/icons/MaterialSymbol';
import { DEBUG_ROUTES_WITH_INLINE_MODALS } from '#/components/dev/constants';
import { useWindowEventListener } from '#/hooks/useEventListener';
import { trackEventTyped } from '#/lib/analytics';
import { useSelector } from '#/reduxState/store';

import ModalGlobalPortal from './ModalGlobalPortal';

/**
 * Module-level counter for auto-incrementing z-index per modal instance
 * to prevent multiple modals incorrectly displayed over each other.
 */
let modalZIndexCounter = 99999;

const WrapBackground = styled.div<{ onClick?: unknown; $zIndex?: number }>`
  position: fixed;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  background: #000000aa;
  z-index: ${({ $zIndex }) => $zIndex ?? 99998};
  overflow: hidden;
  cursor: ${props => (props.onClick ? 'pointer' : 'initial')};

  .simpleModalTransition-appear {
    opacity: 0.1;
  }
  .simpleModalTransition-appear-done {
    opacity: 1;
    transition: opacity 400ms ease-in;
  }
`;

const Wrap = styled.div<{ $zIndex?: number }>`
  /* Disable events, and reenable inside Modal content: to fix issue with start drag (eg. text select) and moving outside would Close modal (on mouse release) */
  pointer-events: none;
  position: fixed;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  z-index: ${({ $zIndex }) => $zIndex ?? 99999};
  /* backdrop-filter: blur(4px); */
  overflow: auto;
  overflow-x: hidden;
  /* https://stackoverflow.com/questions/33454533/cant-scroll-to-top-of-flex-item-that-is-overflowing-container */
  display: flex;

  .simpleModalTransition-appear {
    opacity: 0.1;
  }
  .simpleModalTransition-appear-done {
    opacity: 1;
    transition: opacity 400ms ease-in;
  }
`;

type ModalSize = 'small' | 'medium' | 'large';

const Modal = styled.div<{
  $isMobile: boolean;
  $size: ModalSize;
  $minHeight?: string;
}>`
  width: ${props =>
    props.$isMobile
      ? 'initial'
      : props.$size === 'large'
      ? '700px'
      : props.$size === 'medium'
      ? '560px'
      : '450px'};
  max-width: 95vw;
  max-height: 95vh;
  min-height: ${({ $minHeight }) => $minHeight ?? 'initial'};
  overflow-y: auto;
  overflow-x: hidden;
  background: var(--elevation-light);
  border: 1px solid var(--elevation-light-border);
  border-radius: 4px;
  color: var(--white);
  position: relative;
  pointer-events: initial;
`;

const Body = styled.div<{ $padding?: string }>`
  padding: ${({ $padding }) => $padding ?? '24px'};
  font-size: var(--medium);
`;

const CloseIconWrap = styled.div`
  position: absolute;
  right: -5px;
  top: -10px;
  padding: 20px;
  color: #112233;
  cursor: pointer;
  &:hover {
    filter: brightness(1.2);
  }
`;

const Header = styled.div<{ $closeable: boolean }>`
  padding: 4px ${({ $closeable }) => ($closeable ? '75px' : '30px')} 4px 24px;

  font-size: var(--medium2);
  position: relative;

  display: flex;
  justify-content: space-between;
  align-items: center;

  background: var(--mainDark);
  border-bottom: 1px solid var(--elevation-light-border);
  height: 56px;
`;

const HeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 5px;

  /* Prevent long texts overflowing Close icon */
  max-width: 100%;
  text-align: left;
  white-space: normal;

  > h2 {
    padding: 0;
  }
`;

const HeaderCloseIconWrap = styled(CloseIconWrap)`
  right: 4px;
  top: 0;
  height: 100%;
  display: flex;
  align-items: center;
  padding-top: 0;
  padding-bottom: 0;
`;

const SimpleModalHeader = ({
  children,
  onClose,
  onBack,
}: {
  children: ReactNode;
  onClose?: () => void;
  onBack?: () => void;
}) => {
  return (
    <Header $closeable={!!onClose}>
      <HeaderLeft>
        {onBack && <BackIcon onClick={onBack} />}
        {children}
      </HeaderLeft>

      {onClose && (
        <HeaderCloseIconWrap onClick={onClose}>
          <MaterialSymbol icon="close" color="var(--white)" />
        </HeaderCloseIconWrap>
      )}
    </Header>
  );
};

export type SimpleModalProps = {
  title?: ReactNode;
  onClose?: () => void;
  disableCloseOnClickOutside?: boolean;
  size: ModalSize;
  children: ReactNode;
  footerChildren?: ReactNode;
  onBack?: () => void;
  className?: string;
  appearAnimation?: boolean;
  // TODO: padding: '0' as default as it is most used option now ?
  padding?: string;
  zIndex?: number;
  minHeight?: string;
  /** Submit handling which is used with submit buttons inside modal */
  formOnSubmit?: () => void;
};

/** Modal componenent rendered in Portal into global DOM */
export default function SimpleModal({
  title,
  onClose,
  children,
  footerChildren,
  disableCloseOnClickOutside,
  onBack,
  size,
  className,
  appearAnimation,
  padding,
  zIndex,
  minHeight,
  formOnSubmit,
}: SimpleModalProps) {
  const [localZIndex] = useState(() => {
    modalZIndexCounter += 2; // Background + Modal
    return modalZIndexCounter;
  });

  useEffect(() => {
    const titleOnlyString = typeof title === 'string' ? title : undefined;
    trackEventTyped({ event: 'modal_open', title: titleOnlyString, size });
    return () => {
      trackEventTyped({ event: 'modal_close', title: titleOnlyString, size });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const modalRef = useRef() as MutableRefObject<HTMLDivElement>;
  const isMobile = useSelector(state => state.screen.isMobile);
  const { pathname } = useLocation();

  const devInlineModal = useMemo(
    () => DEBUG_ROUTES_WITH_INLINE_MODALS.includes(pathname),
    [pathname]
  );

  useWindowEventListener(
    'keydown',
    onClose
      ? e => {
          if (e.key === 'Escape') {
            onClose();
          }
        }
      : undefined
  );

  const modalCore = (
    <Modal
      ref={modalRef}
      $isMobile={isMobile}
      $size={size}
      $minHeight={minHeight}
    >
      {!title && onClose && (
        <CloseIconWrap tabIndex={0} onClick={onClose}>
          <MaterialSymbol icon="close" color="var(--white)" />
        </CloseIconWrap>
      )}
      {title && (
        <SimpleModalHeader onClose={onClose} onBack={onBack}>
          <h2>{title}</h2>
        </SimpleModalHeader>
      )}
      <Body className={className} $padding={padding}>
        {children}
      </Body>
    </Modal>
  );

  const modal = formOnSubmit ? (
    <form
      onSubmit={event => {
        event.preventDefault();
        formOnSubmit();
      }}
    >
      {modalCore}
    </form>
  ) : (
    modalCore
  );

  return (
    <ModalGlobalPortal>
      {!devInlineModal && (
        <WrapBackground
          onClick={disableCloseOnClickOutside ? undefined : onClose}
          $zIndex={(zIndex ?? localZIndex) - 1}
        />
      )}
      <Wrap
        style={
          devInlineModal ? { position: 'relative', padding: 30 } : undefined
        }
        $zIndex={zIndex ?? localZIndex}
      >
        <div style={{ margin: 'auto' }}>
          {appearAnimation ? (
            <CSSTransition
              nodeRef={modalRef}
              in
              timeout={1}
              classNames="simpleModalTransition"
              unmountOnExit
              appear
            >
              {modal}
            </CSSTransition>
          ) : (
            modal
          )}
        </div>
      </Wrap>
      {footerChildren || null}
    </ModalGlobalPortal>
  );
}
